import { Alert, Box, Dialog, DialogActions, DialogContent, DialogTitle, Stack, Typography } from '@mui/material';
import { McpAccessProfileWithToken } from '../../../Api/McpAccess';
import { CopyButton } from '../../Atoms/CopyButton';
import { DEFAULT_ADVANCE_BUTTON_STYLE } from '../../../utils/styleHelpers';

export interface McpAccessTokenRevealModalProps {
  open: boolean;
  profile: McpAccessProfileWithToken | null;
  setupCommand: string;
  onClose: () => void;
  onCopyFailed?: (message: string) => void;
  onCopied?: (label: string) => void;
}

export const McpAccessTokenRevealModal = ({
  open,
  profile,
  setupCommand,
  onClose,
  onCopyFailed,
  onCopied
}: McpAccessTokenRevealModalProps) => {
  if (!profile) return null;

  const handleClose = (_event: object, reason: 'backdropClick' | 'escapeKeyDown') => {
    if (reason === 'backdropClick' || reason === 'escapeKeyDown') return;
    onClose();
  };

  return (
    <Dialog
      // Pendo records the text of whatever is clicked, and a click almost anywhere in here lands on the token.
      className="pendo-ignore"
      open={open}
      onClose={handleClose}
      disableEscapeKeyDown
      maxWidth="sm"
      fullWidth
      aria-labelledby="mcp-token-reveal-title"
      aria-describedby="mcp-token-reveal-warning"
      data-testid="mcp-token-reveal-modal">
      <DialogTitle id="mcp-token-reveal-title">Your MCP access token</DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Alert
            severity="warning"
            id="mcp-token-reveal-warning"
            data-testid="mcp-token-reveal-warning">
            You will not be able to see this token again. Copy it now and store it securely.
          </Alert>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary">
              Token for <strong>{profile.title}</strong>
            </Typography>
            <Box
              sx={{
                p: 1.5,
                mt: 0.5,
                borderRadius: 1,
                bgcolor: 'grey.100',
                wordBreak: 'break-all',
                userSelect: 'all'
              }}>
              <code
                data-testid="mcp-token-value"
                data-dd-privacy="mask"
                data-dd-action-name="MCP token"
                style={{ fontFamily: 'monospace', fontSize: '0.9rem', userSelect: 'all' }}>
                {profile.token}
              </code>
            </Box>
            <Box sx={{ mt: 1 }}>
              <CopyButton
                value={profile.token}
                label="Copy token"
                ariaLabel="Copy token"
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={true}
                dataTestId="copy-mcp-token"
                onCopied={() => onCopied?.('token')}
                onCopyFailed={onCopyFailed}
              />
            </Box>
          </Box>

          <Box>
            <Typography
              variant="caption"
              color="text.secondary">
              Setup command (paste into a terminal)
            </Typography>
            <Box
              sx={{
                p: 1.5,
                mt: 0.5,
                borderRadius: 1,
                bgcolor: 'grey.100',
                wordBreak: 'break-all',
                userSelect: 'all'
              }}>
              <code
                data-testid="mcp-setup-command-value"
                data-dd-privacy="mask"
                data-dd-action-name="MCP setup command"
                style={{ fontFamily: 'monospace', fontSize: '0.85rem', userSelect: 'all' }}>
                {setupCommand}
              </code>
            </Box>
            <Box sx={{ mt: 1 }}>
              <CopyButton
                value={setupCommand}
                label="Copy setup command"
                ariaLabel="Copy setup command"
                dataTestId="copy-mcp-setup-command"
                onCopied={() => onCopied?.('setup command')}
                onCopyFailed={onCopyFailed}
              />
            </Box>
          </Box>
        </Stack>
      </DialogContent>
      <DialogActions>
        <button
          type="button"
          onClick={onClose}
          data-testid="mcp-token-reveal-close"
          className={DEFAULT_ADVANCE_BUTTON_STYLE}>
          I&apos;ve saved the token
        </button>
      </DialogActions>
    </Dialog>
  );
};
